import { useMsalToken } from "@/hooks/use-msal-token";
import { API_ENDPOINTS, TAG_BUILDER } from "./abstractions/api-types";
import { useAuthenticatedApi } from "./abstractions/use-authenticated-api";
import type {
  DownloadPaymentEvidenceRequest,
  DownloadPaymentEvidenceResponse,
  PaymentStatsResponse
} from "@/domain/payments";
import { buildHttpGet, buildHttpGetForDownload } from "@/utilities/api-utils";
import { config } from "@/config/environment";
import { DEFAULT_QUERY_OPTIONS } from "./abstractions/api-config";

/**
 * The Payments API group — the endpoints whose paths are not scoped to a role.
 *
 * Evidence download is authorised against the payment itself, because both parties
 * need it: the practice to show what it sent, the professional to see what the claim
 * rests on. Stats are tenant-wide and internal-staff only. Neither belongs on
 * `practices-api` or `professionals-api`.
 */
export function useAuthenticatedPaymentsApi() {
  const authenticatedApi = useAuthenticatedApi();
  const { addAuthHeader } = useMsalToken();

  const paymentsApi = authenticatedApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      // Download the proof of payment attached to one worked shift date
      downloadPaymentEvidence: builder.query<
        DownloadPaymentEvidenceResponse,
        DownloadPaymentEvidenceRequest
      >({
        query: async ({ paymentId }) =>
          await buildHttpGetForDownload(
            config,
            addAuthHeader,
            API_ENDPOINTS.PAYMENTS.EVIDENCE(paymentId)
          ),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Tenant-wide payment and fee counters (admin / support)
      getPaymentStats: builder.query<PaymentStatsResponse, void>({
        query: async () =>
          await buildHttpGet(config, addAuthHeader, API_ENDPOINTS.PAYMENTS.STATS),
        providesTags: () => [TAG_BUILDER.PAYMENT.LIST()],
        ...DEFAULT_QUERY_OPTIONS
      })
    })
  });

  return {
    useLazyDownloadPaymentEvidenceQuery:
      paymentsApi.useLazyDownloadPaymentEvidenceQuery,
    useGetPaymentStatsQuery: paymentsApi.useGetPaymentStatsQuery
  };
}
